import { type ReactNode, type RefObject, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface DetailViewProps {
  children: ReactNode;
  className?: string;
  title?: string;
  actions?: ReactNode;
  /** Content rendered below the title row (e.g. inline assignments, social actions) */
  belowTitle?: ReactNode;
  /** Navigation breadcrumb above the title (e.g. "← Products") */
  backNav?: ReactNode;
  /** Subtitle row rendered directly under the title (e.g. status badge, SKU) */
  subtitle?: ReactNode;
  /** Media thumbnail rendered to the left of the title */
  media?: ReactNode;
  /**
   * How `media` renders in the MOBILE header:
   *  - "hero" (default): full-width aspect-square image — right for product PDPs.
   *  - "inline": small avatar beside the title — right for people/customers,
   *    whose small avatar would otherwise float in a huge empty square.
   */
  mediaLayout?: "hero" | "inline";
  /** Badge rendered inline to the right of the title text */
  titleBadge?: ReactNode;
  /** Tab bar rendered below belowTitle, above the content scroll area */
  tabBar?: ReactNode;
  /** When true, hides the tab bar (used when tabs are rendered externally, e.g. panel sidebar) */
  hideTabBar?: boolean;
  /** Extra classes applied to the content scroll area (e.g. max-w constraint) */
  contentClassName?: string;
  disableScroll?: boolean;
  /** Ref forwarded to the scrollable content container */
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** Social actions row rendered below the title (e.g. heart, comment, follow, share) */
  belowActions?: ReactNode;
  /**
   * When the parent renders its own sticky header bar above the content (e.g. the
   * full-page detail top bar, h-14), the sticky tab bar must stick BELOW it so the
   * two don't overlap. Offsets account for the app-shell main's md:pt-4 (16px).
   */
  hasStickyHeaderBar?: boolean;
  /**
   * When the parent already supplies the page's horizontal padding (e.g. a
   * PageContainer with `md:p-6`), set this so the header doesn't add its own
   * `px-6` on top — otherwise the left/right padding is doubled. The parent is
   * then the single source of truth for horizontal padding. (Content and tab
   * padding are already parent-controlled via `contentClassName` / `tabBar`.)
   */
  contentPaddedExternally?: boolean;
}

export function DetailView({
  children,
  className,
  title,
  actions,
  belowTitle,
  belowActions,
  backNav,
  subtitle,
  media,
  titleBadge,
  tabBar,
  hideTabBar = false,
  contentClassName,
  disableScroll = false,
  scrollRef,
  hasStickyHeaderBar = false,
  contentPaddedExternally = false,
  mediaLayout = "hero",
}: DetailViewProps) {
  const hasHeader = !!(title || actions || belowTitle || backNav);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const internalRef = useRef<HTMLDivElement | null>(null);

  const setRefs = useCallback((node: HTMLDivElement | null) => {
    internalRef.current = node;
    if (scrollRef && 'current' in scrollRef) {
      (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  }, [scrollRef]);

  return (
    <div className={cn("h-full flex flex-col", className)}>
      <div
        ref={setRefs}
        className={cn(
          "flex-1 pb-5 scrollbar-hide",
          disableScroll
            ? "overflow-hidden"
            : "overflow-y-auto overflow-x-hidden",
        )}
      >
        {/* Header — scrolls away with content */}
        {hasHeader && (
          <div className="border-b border-border/40">
            {isDesktop ? (
              /* ── Desktop header: original side-by-side layout ── */
              <div className={cn("pt-4 pb-4", (hasStickyHeaderBar || contentPaddedExternally) ? "px-0" : "px-6")}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {backNav && <div className="mb-2">{backNav}</div>}
                    <div className="flex items-center gap-3">
                      {media}
                      {title && (
                        <h1 className="text-[26px] font-bold tracking-tight truncate leading-tight min-w-0">{title}</h1>
                      )}
                      {titleBadge}
                      {belowTitle}
                    </div>
                    {subtitle && (
                      <div className="flex items-center gap-3 mt-2 min-w-0 overflow-clip">
                        {subtitle}
                      </div>
                    )}
                  </div>
                  {actions && <div className="flex items-center gap-2 shrink-0 pt-5">{actions}</div>}
                </div>
                {belowActions && (
                  <div className="mt-3">{belowActions}</div>
                )}
              </div>
            ) : mediaLayout === "inline" ? (
              /* ── Mobile header (inline media): small avatar beside the title.
                   For people/customers — avoids the empty aspect-square hero.
                   Actions sit top-right, above the avatar. ── */
              <div className="px-2 pt-4 pb-4 space-y-4">
                {actions && (
                  <div className="flex items-center justify-end gap-2">{actions}</div>
                )}
                <div className="flex items-center gap-3">
                  {media}
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    {title && (
                      <h1 className="min-w-0 text-xl font-bold tracking-tight leading-snug line-clamp-2">{title}</h1>
                    )}
                    {titleBadge}
                  </div>
                </div>
                {subtitle && <div className="min-w-0 overflow-clip">{subtitle}</div>}
                {belowActions && <div>{belowActions}</div>}
                {belowTitle && <div>{belowTitle}</div>}
              </div>
            ) : (
              /* ── Mobile header: stacked vertical layout (Shopee PDP style) ── */
              <div>
                {/* Hero image — full-width edge-to-edge, aspect-ratio square */}
                {media && (
                  <div className="w-full aspect-square bg-gray-50 dark:bg-gray-900 mb-4">
                    {media}
                  </div>
                )}

                {/* Content below image — padded */}
                <div className="px-2 pb-4 space-y-4">
                  {/* Title + badge */}
                  <div className="flex items-start gap-2">
                    {title && (
                      <h1 className="text-lg font-bold tracking-tight leading-snug line-clamp-2 flex-1">{title}</h1>
                    )}
                    {titleBadge}
                  </div>

                  {/* Social actions (heart, comment, follow, share) — above assignments */}
                  {belowActions && <div>{belowActions}</div>}

                  {/* Assignments (belowTitle) */}
                  {belowTitle && <div>{belowTitle}</div>}

                  {/* Action buttons */}
                  {actions && (
                    <div className="flex items-center gap-2">{actions}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {/* Tab bar — sticky within scroll container. No own background so it
            inherits the page/main canvas (bg is managed in one place, the main
            container), instead of a hardcoded gray layer. */}
        {tabBar && !hideTabBar && (
          <div
            className={cn(
              "sticky z-[5] md:z-20 bg-inherit",
              // When a sticky header bar (h-14) sits above, stick the tabs below
              // it so they don't overlap. md offset also cancels the app-shell
              // main's md:pt-4 (56px bar - 16px pad = 40px = top-10).
              hasStickyHeaderBar ? "top-14 md:top-10" : "top-0",
            )}
          >
            {tabBar}
          </div>
        )}
        <div className={cn("space-y-4", contentClassName)}>
          {children}
        </div>
      </div>
    </div>
  );
}
